import React, { useState } from 'react';
import { Button, DatePicker, Dropdown } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(utc);
dayjs.extend(isoWeek);

const { RangePicker } = DatePicker;

export const RANGE_LABEL = { today: 'Today', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly', custom: 'Custom', all: 'All' };

/**
 * The period a widget shows, as the dates its query takes - the same UTC day / ISO week / month
 * bounds the dashboard has always used. "all" has no bounds (the server skips its date filter).
 */
export const rangeDates = (range, from, to) => {
  switch (range) {
    case 'weekly':
      return { startDate: dayjs.utc().startOf('isoWeek').startOf('day').toDate(), endDate: dayjs.utc().endOf('isoWeek').endOf('day').toDate() };
    case 'monthly':
      return { startDate: dayjs.utc().startOf('month').toDate(), endDate: dayjs.utc().endOf('month').toDate() };
    case 'yearly':
      return { startDate: dayjs.utc().startOf('year').toDate(), endDate: dayjs.utc().endOf('year').toDate() };
    case 'custom':
      if (from && to) return { startDate: dayjs.utc(from).startOf('day').toDate(), endDate: dayjs.utc(to).endOf('day').toDate() };
      return { startDate: dayjs.utc().startOf('day').toDate(), endDate: dayjs.utc().endOf('day').toDate() };
    case 'all':
      return { startDate: undefined, endDate: undefined };
    case 'today':
    default:
      return { startDate: dayjs.utc().startOf('day').toDate(), endDate: dayjs.utc().endOf('day').toDate() };
  }
};

// ISO strings for the GraphQL variables (undefined stays undefined for "all")
export const rangeVariables = (settings, fallback = 'today') => {
  const { startDate, endDate } = rangeDates(settings?.range || fallback, settings?.from, settings?.to);
  return { startDate: startDate?.toISOString(), endDate: endDate?.toISOString() };
};

export const rangeLabel = (settings, fallback = 'today') => {
  const range = settings?.range || fallback;
  if (range === 'custom' && settings?.from && settings?.to) {
    return `${dayjs(settings.from).format('DD MMM')} – ${dayjs(settings.to).format('DD MMM')}`;
  }
  return RANGE_LABEL[range] || 'Today';
};

/**
 * The little "Today ▾" badge on a widget: pick a period, or Custom to choose dates right there
 * in the same dropdown. Saved with the widget, so each widget remembers its own period.
 */
export const RangeBadge = ({ settings, onChange, options = ['today', 'weekly', 'monthly', 'custom'], fallback = 'today' }) => {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [dates, setDates] = useState(null);
  const current = settings?.range || fallback;

  const choose = (range) => {
    if (range === 'custom') { setPicking(true); return; }
    onChange({ range });
    setOpen(false);
  };

  return (
    <span onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} className="no-drag">
      <Dropdown
        trigger={['click']}
        open={open}
        onOpenChange={(next) => { setOpen(next); if (!next) setPicking(false); }}
        menu={{ items: options.map((key) => ({ key, label: RANGE_LABEL[key] })), selectedKeys: [current], onClick: ({ key }) => choose(key) }}
        dropdownRender={(menu) => (picking ? (
          <div className="dw-range-picker">
            <div className="dw-range-picker-title">Choose dates</div>
            <RangePicker
              size="small"
              defaultValue={settings?.from && settings?.to ? [dayjs(settings.from), dayjs(settings.to)] : null}
              onChange={(value) => setDates(value)}
              getPopupContainer={(node) => node.parentNode}
            />
            <div className="dw-range-picker-actions">
              <Button size="small" onClick={() => setPicking(false)}>Back</Button>
              <Button
                size="small"
                type="primary"
                disabled={!dates?.[0] || !dates?.[1]}
                onClick={() => {
                  onChange({ range: 'custom', from: dates[0].format('YYYY-MM-DD'), to: dates[1].format('YYYY-MM-DD') });
                  setPicking(false);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        ) : menu)}
      >
        <span className="metric-badge clickable" data-range-badge>
          {rangeLabel(settings, fallback)} <DownOutlined style={{ fontSize: '10px' }} />
        </span>
      </Dropdown>
    </span>
  );
};
